import { useStorage } from '@vueuse/core';
import gql from 'graphql-tag';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, ref } from 'vue';
import apolloClient from '@/apollo/client';
import { useCompanyStore } from '@/stores/company';
import type { Location } from '@/types';
import { DataScope } from '@/types';

const GET_LOCATIONS = gql`
  query getLocations {
    locations {
      address
      bankAccount
      bookable
      fullAddress
      id
      internalName
      name
      sortOrder
      city
      postalcode
      publicId
    }
  }
`;

export const useLocationsStore = defineStore('locations', () => {
  const routeLocationId = ref<string>('');
  const locations = ref<Location[]>([]);
  const dataScope = useStorage<DataScope>('location_context', DataScope.Local);

  if (
    dataScope.value !== DataScope.Global &&
    dataScope.value !== DataScope.Local
  ) {
    // Make sure to set the correct value if somehow the user has a custom value set in the localStorage
    dataScope.value = DataScope.Local;
  }

  const locationId = computed<Location['id'] | undefined>(() => {
    const { company } = useCompanyStore();
    const primaryLocationId = company.primaryLocationId;
    const parsedRouteId = Number.parseInt(routeLocationId.value);
    const isValid =
      !!parsedRouteId &&
      locations.value.some((location) => location.id === parsedRouteId);

    return isValid ? parsedRouteId : primaryLocationId;
  });

  const location = computed<Location | undefined>(() =>
    locations.value.find((location) => location.id === locationId.value),
  );

  const locationIds = computed<Location['id'][]>(() => {
    if (!locationId.value) {
      return [];
    }

    const { multiLocation } = useCompanyStore();
    return multiLocation
      ? locations.value.map((location) => location.id)
      : [locationId.value];
  });

  const locationById = (id: Location['id']): Location | undefined =>
    locations.value.find((location) => location.id === id);

  const getLocations = async (): Promise<void> => {
    const { data } = await apolloClient.query<{ locations: Location[] }>({
      query: GET_LOCATIONS,
    });

    locations.value = data.locations;
  };

  const reset = () => {
    locations.value = [];
    dataScope.value = DataScope.Local;
  };

  return {
    routeLocationId,
    locations,
    dataScope,
    locationId,
    location,
    locationIds,
    reset,
    locationById,
    getLocations,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLocationsStore, import.meta.hot));
}
